import { Link, useParams } from 'react-router-dom'
import { ArrowRight, ArrowLeft, CheckCircle, Shield } from 'lucide-react'

// ─── ARTICLE DATA ────────────────────────────────────────────────────────────

const ARTICLES = [
  {
    slug: 'do-i-need-to-register-for-mtd-itsa',
    title: 'Do I need to register for MTD ITSA?',
    metaDesc: 'Not sure if Making Tax Digital Income Tax Self Assessment applies to you? This guide explains exactly who needs to register, when, and what happens if you don\'t.',
    date: '2026-04-01',
    readTime: '4 min read',
    category: 'MTD Basics',
    content: `
Making Tax Digital for Income Tax Self Assessment — MTD ITSA — is HMRC's new digital tax system that replaces the annual self-assessment tax return with quarterly digital updates. If you're self-employed or a landlord, the question of whether it applies to you is one of the most important tax questions you'll face this decade.

## Who needs to register for MTD ITSA?

MTD ITSA is being rolled out in phases based on your annual income from self-employment and property:

**From April 2026** — sole traders and landlords with income over £50,000 must comply.

**From April 2027** — the threshold drops to £30,000. Anyone earning over £30,000 from self-employment or property must also join.

**From April 2028** — the threshold drops again to £20,000, bringing the vast majority of sole traders and landlords into scope.

If your combined income from self-employment and property crosses these thresholds, you are legally required to use MTD-compatible software and submit quarterly updates to HMRC.

## What counts as income for MTD ITSA purposes?

HMRC looks at your gross income — that's your total receipts before any expenses are deducted. For a self-employed person, that means all your invoices and payments received. For a landlord, it means your total rental income before mortgage interest, maintenance costs and other allowable expenses.

If you have both self-employment and rental income, they are added together for the purposes of calculating whether you meet the threshold.

## What if I'm below the threshold right now?

If your income is currently below £50,000, you don't need to register yet — but the threshold is dropping. If you're between £20,000 and £50,000, you will be brought in by 2028. It makes sense to start using MTD-compatible software now so the transition isn't a last-minute scramble.

## What happens if I don't register when required?

HMRC will issue penalties for non-compliance. The system works on a points basis — each missed or late submission earns points, and once you hit a threshold, a £200 fine is triggered. Points accumulate over time, meaning persistent non-compliance leads to significant financial penalties.

There is no opt-out for people who meet the income threshold.

## How do I actually register?

Registration for MTD ITSA is done through your HMRC online account. You'll need:

- Your Government Gateway user ID and password
- Your National Insurance number
- Your Unique Taxpayer Reference (UTR)
- MTD-compatible software already set up — HMRC will ask you to confirm you have this before completing registration

You need to register at least two weeks before your first quarterly filing deadline to give HMRC time to process it.

## What are the quarterly filing deadlines?

Under MTD ITSA, the tax year is split into four quarters with the following submission deadlines:

- **Quarter 1** (6 April – 5 July): due 7 August
- **Quarter 2** (6 July – 5 October): due 7 November
- **Quarter 3** (6 October – 5 January): due 7 February
- **Quarter 4** (6 January – 5 April): due 7 May

You also need to submit an End of Period Statement and a Final Declaration (replacing the old annual tax return) each year.

## Do I still need an accountant?

Not necessarily. MTD ITSA is designed to be completed using software, without requiring an accountant for the quarterly submissions themselves. The quarterly updates are straightforward — your income and expenses for the period, categorised correctly.

Where an accountant remains valuable is in year-end planning, checking your Final Declaration, and ensuring you're claiming every allowable expense. But the day-to-day quarterly admin can be handled by software built specifically for sole traders.

Quarterly is built for exactly this — connecting your bank, categorising transactions automatically, and guiding you through each quarterly submission in plain English.
    `,
  },
  {
    slug: 'mtd-itsa-deadlines-2026',
    title: 'MTD ITSA deadlines 2026/27 — every date you need',
    metaDesc: 'The complete list of Making Tax Digital ITSA deadlines for 2026/27. Quarterly filing dates, payment deadlines, and what happens if you miss them.',
    date: '2026-04-01',
    readTime: '3 min read',
    category: 'MTD Basics',
    content: `
Missing an MTD ITSA deadline means penalty points, and penalty points mean fines. Here is every date you need for the 2026/27 tax year — save this page.

## The four quarterly submission deadlines

Under MTD ITSA, you submit a digital update to HMRC four times a year. Each update covers a specific quarter of the tax year and must be submitted within five weeks of the quarter ending.

**Quarter 1: 6 April 2026 to 5 July 2026**
Submission deadline: **7 August 2026**

**Quarter 2: 6 July 2026 to 5 October 2026**
Submission deadline: **7 November 2026**

**Quarter 3: 6 October 2026 to 5 January 2027**
Submission deadline: **7 February 2027**

**Quarter 4: 6 January 2027 to 5 April 2027**
Submission deadline: **7 May 2027**

## End of Period Statement (EOPS)

After the tax year ends on 5 April, you need to submit your End of Period Statement. This confirms your total income and expenses for the year and includes any adjustments — claiming for use of home as office, prior year losses, and similar.

**EOPS deadline: 31 January 2028** (for the 2026/27 tax year)

## Final Declaration

The Final Declaration replaces the old annual self-assessment tax return. It pulls together all your income sources — self-employment, property, employment, dividends, savings — and calculates your final tax bill.

**Final Declaration deadline: 31 January 2028**

## Payment deadlines

Paying on time is separate from submitting on time. HMRC's payment schedule for 2026/27:

**Payments on account** (if required): 31 January 2027 and 31 July 2027
**Balancing payment** for 2026/27: 31 January 2028

Payments on account are required if your previous year's tax bill exceeded £1,000 and less than 80% was collected through PAYE. If this applies to you, you pay half your estimated bill in January and half in July.

## What happens if you miss a deadline?

MTD ITSA uses a points-based penalty system. Each late submission earns one penalty point. Once you accumulate enough points, HMRC issues a £200 fine:

- **Quarterly filers** (that's you): penalty triggered at 4 points
- Points reset after a clean compliance period of 24 months

Late payment penalties are separate and calculated as a percentage of the unpaid tax, starting at 2% after 15 days.

## How to make sure you never miss a deadline

The simplest approach is to use MTD-compatible software that tracks your deadlines automatically. Quarterly shows every upcoming filing date and payment deadline on your dashboard, and sends reminders before each one hits.

You set aside 20 minutes at the end of each quarter, review your categorised transactions, and submit. Done.
    `,
  },
  {
    slug: 'what-counts-as-business-expense-sole-trader',
    title: 'What counts as a business expense for sole traders?',
    metaDesc: 'A complete guide to allowable business expenses for UK sole traders — from mileage and home office to equipment and meals. Written by Chartered Accountants.',
    date: '2026-04-01',
    readTime: '6 min read',
    category: 'Tax Guides',
    content: `
Getting your expenses right is one of the most valuable things you can do as a sole trader. Every legitimately claimed expense reduces your taxable profit, which reduces your tax bill. But claim something you shouldn't and you risk an HMRC enquiry.

Here is a plain-English guide to the main categories of allowable business expenses for UK sole traders, written by Chartered Accountants.

## The golden rule

HMRC allows you to deduct expenses that are "wholly and exclusively" incurred for the purposes of your trade. If an expense has a dual purpose — part business, part personal — you can only claim the business proportion. If it's entirely personal, you cannot claim it at all.

## Office and home working costs

If you work from home, you can claim a proportion of your household bills as a business expense. There are two methods:

**Simplified flat rate** — HMRC allows a fixed monthly deduction based on hours worked at home:
- 25-50 hours per month: £10
- 51-100 hours per month: £18
- Over 100 hours per month: £26

**Actual costs** — You calculate the business proportion of your rent or mortgage interest, utilities, broadband and council tax based on the number of rooms used for work and the time spent working. This requires more record-keeping but often produces a higher deduction.

## Travel and mileage

If you use your own vehicle for business travel, you can claim the HMRC Approved Mileage Allowance Payments (AMAP) rate:

- **First 10,000 business miles per year**: 45p per mile
- **Over 10,000 miles**: 25p per mile

You must keep a mileage log recording the date, destination, purpose and miles for every business journey. Commuting from home to a regular workplace does not count as business mileage.

Public transport, parking, tolls and congestion charges are also allowable when incurred for business purposes.

## Equipment and tools

Anything you buy primarily for your business — laptops, tools, specialist equipment, cameras, instruments — is an allowable expense. Under the Annual Investment Allowance, you can deduct 100% of the cost in the year of purchase rather than spreading it over several years.

Items with a significant personal use element (a laptop used 60% for business and 40% personal) should only be claimed at the business proportion.

## Phone and broadband

If you use your mobile or broadband for business, you can claim the business proportion of the bill. If your phone is used roughly 50/50, claim 50%. If you have a dedicated business phone, claim 100%.

## Meals and subsistence

Meals are one of the most misunderstood expense categories. The rule is:

You can claim subsistence — meals and drinks — when you are travelling away from your normal place of work on a business trip. You cannot claim for your regular lunch or coffee when working at your normal location.

There is no percentage rule for meals under self-assessment — the 50% rule applies to VAT, not income tax. If a business meal is wholly and exclusively for business, claim the full amount.

## Professional services

Accountancy fees, legal fees directly related to your business, professional subscriptions and indemnity insurance are all allowable. Your membership of a professional body relevant to your trade is also deductible.

## Marketing and advertising

Website costs, advertising, business cards, social media ads, PR, and any other marketing spend directly promoting your business are fully allowable.

## Software and subscriptions

Business software, cloud storage, project management tools, and industry-specific subscriptions are allowable. If a subscription is partly personal (Spotify, Netflix), it is not claimable.

## Training and professional development

Training costs that maintain or improve skills relevant to your current trade are allowable. A plumber taking a course on new boiler technology — allowable. A plumber taking a course to become a solicitor — not allowable, as it would be for an entirely new trade.

## What you cannot claim

Common expenses that sole traders mistakenly try to claim include:

- Everyday clothing (even if you wear it to work) unless it is a uniform, safety equipment or protective gear
- The cost of commuting between home and a regular workplace
- Personal entertainment — client entertaining has specific rules under income tax
- Fines and penalties including speeding tickets and parking tickets
- The personal portion of any dual-purpose expense

## Keeping records

You are legally required to keep records of all your business income and expenses for at least five years after the 31 January submission deadline. Under MTD ITSA, this means digital records — paper receipts need to be photographed and stored.

Quarterly stores your receipts against the corresponding transaction automatically, keeping your records HMRC-ready at all times.
    `,
  },
  {
    slug: 'how-to-file-quarterly-tax-updates-hmrc',
    title: 'How to file quarterly tax updates to HMRC — a step by step guide',
    metaDesc: 'Step-by-step guide to filing MTD ITSA quarterly updates to HMRC. What information you need, how to submit, and what happens after you file.',
    date: '2026-04-01',
    readTime: '5 min read',
    category: 'How-to Guides',
    content: `
Filing quarterly tax updates to HMRC under MTD ITSA is simpler than the old annual self-assessment — but only if you know what you're doing and have the right software. This guide walks you through the process from start to finish.

## What is a quarterly update?

A quarterly update is a digital submission to HMRC summarising your business income and expenses for a three-month period. It is not a tax return — you are not paying tax at this stage. You are simply reporting your figures so HMRC can see an in-year picture of your finances.

HMRC uses this information to update your projected tax liability throughout the year, helping you (and them) avoid the January shock of discovering you owe a large sum you haven't set aside.

## What you need before you start

Before submitting your first quarterly update, you need:

**MTD-compatible software** — You cannot submit directly through HMRC's website. You must use software that connects to the MTD API. Quarterly does this.

**An MTD ITSA registration** — You must have registered for MTD ITSA through your HMRC online account. If you haven't done this yet, register at least two weeks before your first deadline.

**Your income and expense records for the quarter** — Under MTD, these must be digital records. If you've connected your bank account to Quarterly, your transactions import automatically.

## Step 1: Gather your transactions

For the quarterly period (for example, 6 April to 5 July), you need a complete record of:

- All business income received
- All allowable business expenses paid

If you've been categorising transactions throughout the quarter in Quarterly, this is already done. If you're starting fresh, you'll need to go through your bank statements and categorise each transaction.

## Step 2: Categorise to HMRC expense types

Every expense must be assigned to an HMRC-recognised category. These include:

- Cost of goods sold
- Construction industry costs
- Employee costs
- Premises costs
- Repairs and maintenance
- General and administrative costs
- Motor expenses
- Travel and subsistence
- Advertising, promotion and entertainment
- Legal and professional costs
- Interest on bank and other loans
- Other business expenses

Using the correct categories matters — HMRC uses this breakdown to assess the reasonableness of your claims.

## Step 3: Review your figures

Before submitting, review your totals. Check that:

- Income matches what you actually received in the period
- Large or unusual expenses have receipts attached
- No personal transactions have been included by mistake
- Mileage has been logged and calculated at the correct AMAP rate

## Step 4: Submit through your MTD software

In Quarterly, once you've reviewed and are happy with the figures, you hit Submit. The software connects directly to HMRC's MTD API, transmits your quarterly update, and receives a confirmation reference number.

The whole process — if you've been categorising as you go — takes around 10 minutes.

## Step 5: Note your confirmation

HMRC will send a confirmation once the submission is received. Keep this reference number. Quarterly logs it automatically against the submitted period.

## After submission: what happens next

HMRC updates your projected tax liability based on the submitted figures. You can see this in your HMRC online account. No payment is required at the quarterly stage — payment comes at the end of the year.

At the end of the tax year, you complete an End of Period Statement (confirming and adjusting your figures) and a Final Declaration (your complete picture across all income sources). This is when HMRC calculates your final tax bill.

## Common mistakes to avoid

**Submitting income without expenses** — Some people submit just their income and forget expenses. This inflates your projected tax bill unnecessarily.

**Missing the deadline** — The deadline is 5 weeks after each quarter ends. Miss it and you earn a penalty point.

**Categorising personal expenses as business** — HMRC's system flags anomalies. An unusually high meals figure relative to your income, for example, can trigger a review.

**Not keeping digital records** — Under MTD, paper records are not sufficient. Your records must be digital and linked to your submissions.
    `,
  },
  {
    slug: 'xero-vs-freeagent-vs-quarterly-mtd',
    title: 'Xero vs FreeAgent vs Quarterly for MTD ITSA — which is right for you?',
    metaDesc: 'Comparing Xero, FreeAgent and Quarterly for Making Tax Digital? Here\'s an honest breakdown of what each is designed for and who should use which.',
    date: '2026-04-01',
    readTime: '5 min read',
    category: 'Comparisons',
    content: `
If you're a sole trader or landlord looking for MTD ITSA software, you've probably encountered Xero and FreeAgent alongside newer options. This is an honest comparison — not a sales pitch — of what each product is designed for and who each suits best.

## The fundamental difference

Xero and FreeAgent were built for businesses with employees, VAT, payroll, and complex accounting needs. They were then adapted to support MTD ITSA.

Quarterly was built from the ground up specifically for sole traders and landlords filing under MTD ITSA. That's the only thing it does.

## Xero

**What it is:** Enterprise accounting software used by hundreds of thousands of UK businesses. It has the most features of any accounting platform — payroll, invoicing, multi-currency, inventory, bank reconciliation, and extensive integrations.

**Who it's for:** Limited companies, businesses with employees, VAT-registered businesses with complex accounting requirements. Accountants love it because it gives them complete control.

**MTD ITSA support:** Yes, Xero supports MTD ITSA. But you're accessing that functionality through a product designed for much more complex use cases.

**Price:** From £15/month (Ignite plan) but most sole traders end up on higher tiers. Many sole traders access it free through NatWest or RBS if they have a business account.

**The honest take:** If you run a limited company or have employees, Xero makes sense. If you're a sole trader who just needs to track income, expenses and file quarterly to HMRC, Xero is considerably more software than you need. The learning curve is steep and the interface assumes accounting knowledge.

## FreeAgent

**What it is:** Cloud accounting software popular with UK freelancers and small businesses. More approachable than Xero, with a cleaner interface. Like Xero, it's distributed free to NatWest, RBS and Ulster Bank business account holders.

**Who it's for:** Freelancers and small businesses who want full accounting software — invoicing, expense tracking, corporation tax estimates, payroll. Works well for limited companies too.

**MTD ITSA support:** Yes, fully supported.

**Price:** £19/month standard, though free for NatWest/RBS/Ulster business account holders.

**The honest take:** FreeAgent is genuinely good software for freelancers, especially if you have a NatWest or RBS account and get it free. It's more approachable than Xero. The downside is it's still built around full accounting — you'll encounter concepts like nominal codes, journals and balance sheets that aren't relevant to basic MTD ITSA compliance.

## Quarterly

**What it is:** An MTD ITSA app built specifically for UK sole traders, freelancers and landlords. Not accounting software — a tax compliance tool designed around the quarterly filing workflow.

**Who it's for:** Self-employed people and landlords who want to connect their bank, categorise transactions, and file quarterly updates to HMRC without needing to understand accounting.

**MTD ITSA support:** Yes — it's the entire purpose of the product.

**Price:** £14.99/month. No free tier through banks.

**The honest take:** If MTD ITSA compliance is your primary need and you don't require invoicing, payroll or corporation tax features, Quarterly is the most direct route. The interface is closer to a banking app than accounting software. You don't need to know what a nominal ledger is to use it.

## Which should you choose?

**Choose Xero if:** You run a limited company, have employees, need payroll, or your accountant already uses it.

**Choose FreeAgent if:** You have a NatWest, RBS or Ulster business account (you get it free), or you want full accounting software with invoicing built in.

**Choose Quarterly if:** You're a sole trader or landlord whose main need is MTD ITSA compliance — tracking income, categorising expenses, and filing quarterly. You want something that feels like a banking app, not an accounting platform.

The right answer depends entirely on what you actually need. If you're unsure, try Quarterly free first — it's the lowest friction way to get MTD-compliant.
    `,
  },
  {
    slug: 'mtd-itsa-landlords',
    title: 'MTD ITSA for landlords — what you need to know',
    metaDesc: 'A complete guide to Making Tax Digital for landlords. Does it apply to you? What records do you need? How do quarterly submissions work for rental income?',
    date: '2026-04-01',
    readTime: '5 min read',
    category: 'Landlords',
    content: `
If you receive rental income from one or more properties in the UK, Making Tax Digital for Income Tax Self Assessment will apply to you — and the rules for landlords have some specific considerations worth understanding.

## Does MTD ITSA apply to landlords?

Yes. MTD ITSA applies to landlords whose income from property meets the threshold:

- **From April 2026**: landlords with property income over £50,000 must comply
- **From April 2027**: threshold drops to £30,000
- **From April 2028**: threshold drops to £20,000

If you also have self-employment income, it is combined with your rental income to determine whether you meet the threshold. A landlord earning £30,000 in rent and £25,000 from freelance work would be in scope from April 2026 with combined income of £55,000.

## What counts as rental income for MTD purposes?

Gross rental receipts — the total rent received before any deductions — is what HMRC uses to assess whether you meet the threshold. If you have multiple properties, all rental income is combined.

## What expenses can landlords claim?

Landlords can deduct allowable expenses from their rental income to arrive at their taxable profit. Key allowable expenses include:

**Mortgage interest** — Since April 2020, landlords can no longer deduct mortgage interest directly from rental income. Instead, you receive a 20% tax credit on finance costs. This is handled automatically in Quarterly.

**Letting agent fees** — Fully allowable, including management fees, tenant-finding fees and renewal fees.

**Maintenance and repairs** — Costs of maintaining the property in its existing condition are allowable. Improvements that enhance the property are capital expenditure, not repairs, and treated differently.

**Buildings insurance** — Fully allowable.

**Ground rent and service charges** — Allowable for leasehold properties.

**Accountancy fees** — Fees paid for preparing rental accounts are allowable.

**Replacement of domestic items** — You can claim for replacing furnishings, appliances and kitchenware in furnished properties under the Replacement of Domestic Items Relief.

**Pre-letting expenses** — Some costs incurred before a property is first let may be allowable if they would have been deductible once the letting business started.

## What records do landlords need to keep?

Under MTD ITSA, landlords must keep digital records of:

- All rental income received, by property
- All allowable expenses, with receipts
- Details of each property — address, ownership structure
- Any capital improvements (for future Capital Gains Tax purposes)

Records must be kept for at least five years after the filing deadline.

## How do quarterly submissions work for landlords?

Each quarter, you submit your rental income and expenses to HMRC through MTD-compatible software. The submission covers:

- Total rental income received in the quarter
- Total allowable expenses in the quarter, broken down by category
- Any adjustments

You are not paying tax at the quarterly stage — you are reporting. Tax is calculated and paid at the end of the year via the Final Declaration.

## Property income allowance

If your gross property income is £1,000 or less, you can use the Property Income Allowance and don't need to declare it or file under MTD ITSA. If your income is above £1,000, you must report it even if your expenses wipe out the profit entirely.

## Furnished Holiday Lettings

The Furnished Holiday Lettings (FHL) tax regime, which gave landlords access to more favourable tax treatment, was abolished from April 2025. Properties that previously qualified as FHLs are now treated as ordinary property businesses for tax purposes.

## Portfolio landlords

If you own multiple properties, each property should ideally be tracked separately. Quarterly handles multiple income streams, allowing you to categorise income and expenses by property for a cleaner picture at year end.
    `,
  },
  {
    slug: 'best-tax-app-uber-drivers-uk',
    title: 'Best tax app for Uber drivers UK 2026 — what you actually need',
    metaDesc: 'A guide to managing your tax as an Uber, Bolt or delivery driver in the UK. What expenses you can claim, how MTD ITSA affects you, and the best app for the job.',
    date: '2026-04-01',
    readTime: '4 min read',
    category: 'Specific Audiences',
    content: `
If you drive for Uber, Bolt, Amazon Flex, Deliveroo or any other platform in the UK, you're self-employed in the eyes of HMRC. That means you're responsible for your own tax — and with MTD ITSA now live, the way you report that tax has changed.

## Are you affected by MTD ITSA?

MTD ITSA applies to self-employed people earning over £50,000 from April 2026, dropping to £30,000 in 2027 and £20,000 in 2028. If your gross platform income — before expenses — hits these thresholds, you must use MTD-compatible software and file quarterly.

Even if you're below the threshold now, tracking income and expenses digitally from day one is the smartest move. The threshold will reach you.

## What expenses can Uber and delivery drivers claim?

This is where most drivers leave money on the table. You can claim:

**Mileage** — This is the big one. HMRC's Approved Mileage Allowance rate is 45p per mile for the first 10,000 business miles, then 25p per mile. Every mile you drive for a fare or delivery counts. At 45p a mile, 20,000 business miles a year is a £9,500 deduction — saving you £1,900 in tax at the basic rate.

You must log every business journey — date, destination, miles. Quarterly has a built-in mileage tracker.

**Phone** — If you use your phone for the platform app, navigation and customer communication, the business proportion is allowable. If it's mostly for work, claim 80-90%.

**Phone mount, dash cam, car accessories** — Allowable if used for business.

**Car washes and cleaning** — Allowable as the car is a business tool.

**Platform fees and service charges** — Uber and other platforms take a commission before paying you. Your taxable income is your net receipts after their cut, but you should track gross receipts and fees separately.

**Parking and toll charges incurred on jobs** — Allowable.

**Specialist insurance** — Hire and reward insurance or any additional cover required for commercial driving is allowable.

## What you cannot claim

- The cost of buying your car (though you can claim mileage or capital allowances — seek advice on which is better for your situation)
- Speeding fines and parking tickets
- Personal journeys in the same vehicle
- Normal commuting from home to a pickup point

## Why mileage tracking matters so much

A driver doing 1,500 miles per month — not unusual — accumulates 18,000 business miles per year. At the AMAP rate, that's £8,250 in allowable deductions. At 20% basic rate tax, that's £1,650 back in your pocket. At 40% higher rate, it's £3,300.

Most drivers either don't track mileage at all, or track it poorly and can't produce records if HMRC asks. Quarterly logs every trip automatically — date, miles, purpose — and calculates the correct AMAP deduction.

## How to manage your tax as a platform driver

The fundamentals are the same as any sole trader:

1. Track all platform income (your weekly statements from the app are your records)
2. Log every business mile
3. Keep receipts for phone bills, accessories, insurance
4. Set aside money for your tax bill as you go — roughly 25-30% of your profit is a sensible starting point
5. File your quarterly MTD update on time once you're in scope

Quarterly is designed to handle all of this — it connects to your bank, imports your platform income automatically, tracks mileage in-app, and tells you exactly what to set aside each month.

## What about the trading allowance?

If your gross income from all self-employment is £1,000 or less in a tax year, you can use the £1,000 trading allowance and don't need to file a tax return. Above £1,000, you must register and report.

Most active drivers will be well above this threshold.
    `,
  },
  {
    slug: 'hmrc-approved-mtd-software-2026',
    title: 'HMRC approved MTD software — the complete list for 2026',
    metaDesc: 'Looking for HMRC-approved Making Tax Digital software? Here\'s what HMRC recognition means, how to find approved products, and what to look for as a sole trader.',
    date: '2026-04-01',
    readTime: '4 min read',
    category: 'MTD Basics',
    content: `
When you're searching for MTD ITSA software, you'll come across the term "HMRC approved" or "HMRC recognised." Here's what it actually means, what's on the list, and how to choose the right product for your situation.

## What does HMRC approved mean?

HMRC maintains a list of software products that have been tested and confirmed to work with the MTD API — the technical connection that allows software to submit quarterly updates directly to HMRC's systems.

Being on the HMRC recognised software list means the product can technically submit MTD ITSA updates. It does not mean HMRC has audited the software for accuracy, endorsed the company, or assessed the quality of the product. It's a technical certification, not a quality stamp.

## Where to find the official HMRC list

The official list of MTD ITSA compatible software is published on GOV.UK at the Find software that's compatible with Making Tax Digital for Income Tax page. HMRC updates this list regularly as new products complete testing.

## What the list includes

Products on the list range from full accounting software packages used by thousands of businesses (Xero, FreeAgent, QuickBooks, Sage) to specialist MTD tools built specifically for sole traders and landlords.

The list typically shows:
- The software name and provider
- Whether it supports voluntary or mandated MTD ITSA
- Pricing model (free, paid, freemium)
- Whether it supports bridging (for those who want to keep their own spreadsheets)

## What to look for beyond the list

Being on the HMRC list is the minimum requirement, not the differentiator. When choosing software, the more useful questions are:

**Is it built for my situation?** A sole trader's needs are fundamentally different from a limited company's. Software built for sole traders under MTD ITSA will be far simpler to use than general accounting software that has added MTD functionality.

**Does it connect to my bank?** Open Banking integration means your transactions import automatically. Without it, you're manually entering data — which defeats the purpose of digital record-keeping.

**Does it handle the expenses I need?** Mileage at AMAP rates, home office, the 50% meals rule, rental income specifics for landlords — these should be handled automatically, not left to you to calculate.

**Is the interface genuinely simple?** Some products on the HMRC list are complex accounting platforms wrapped in a simpler interface. Others are built from the ground up for non-accountants. The difference becomes clear the first time you use them.

**What does it cost?** Options range from free (HMRC's own basic tool) to £30+/month for full accounting software. For most sole traders, something in the £10-20/month range gives you everything you need.

## Bridging software

If you want to continue managing your records in a spreadsheet, you can use bridging software — a tool that takes your spreadsheet data and submits it to HMRC in the required digital format. This is a compliant option but adds a step, and you lose the benefit of automatic bank feeds and real-time tax estimates.

## Free options

HMRC offers a basic free tool for the simplest cases. It handles the submission mechanics but provides no bank connection, no automatic categorisation, and no in-year tax estimates. For a sole trader with a small number of simple transactions, it may be sufficient.

## Where Quarterly fits

Quarterly is built specifically for the MTD ITSA workflow — bank connection, automatic categorisation, quarterly submission. It is designed to be the simplest route for sole traders who need to meet their quarterly obligations.

Quarterly is not currently on HMRC's compatible software list. We are working through HMRC's production access process and will update this page when that changes. If you need software that is on the list today, use the GOV.UK link above.
    `,
  },
]

// ─── BLOG INDEX ──────────────────────────────────────────────────────────────

function BlogIndex() {
  const categories = [...new Set(ARTICLES.map(a => a.category))]

  return (
    <div style={{ background: 'var(--navy)', minHeight: '100vh' }}>

      {/* Nav */}
      <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, background: 'rgba(8,18,31,0.94)', backdropFilter: 'blur(18px)', borderBottom: '1px solid var(--navy-border)', padding: '0 clamp(16px,4vw,40px)', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--teal)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: '#08121F', fontWeight: 700 }}>Q</span>
          </div>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: 'var(--white)' }}>Quarterly</span>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Link to="/pricing" style={{ fontSize: 13, color: 'var(--slate)', fontWeight: 500, textDecoration: 'none' }}>Pricing</Link>
          <Link to="/login" style={{ fontSize: 13, color: 'var(--slate)', fontWeight: 500, textDecoration: 'none' }}>Sign in</Link>
          <Link to="/login" className="btn btn-primary btn-sm">Try free</Link>
        </div>
      </nav>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: 'clamp(100px,12vw,130px) clamp(16px,4vw,40px) 80px' }}>

        {/* Header */}
        <div style={{ marginBottom: 56 }}>
          <div style={{ fontSize: 11, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '.14em', fontWeight: 700, marginBottom: 14 }}>MTD ITSA Guides</div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(32px,5vw,52px)', color: 'var(--white)', marginBottom: 16, lineHeight: 1.1 }}>
            Making Tax Digital,<br />explained plainly.
          </h1>
          <p style={{ fontSize: 16, color: 'var(--slate)', lineHeight: 1.7, maxWidth: 520 }}>
            Guides written by Chartered Accountants for UK sole traders, freelancers and landlords navigating MTD ITSA.
          </p>
        </div>

        {/* Articles grid */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {ARTICLES.map((article, i) => (
            <Link key={article.slug} to={`/blog/${article.slug}`} style={{ textDecoration: 'none' }}>
              <div style={{ padding: '24px 0', borderBottom: '1px solid var(--navy-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24, transition: 'opacity .15s' }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <span style={{ fontSize: 11, color: 'var(--teal)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em' }}>{article.category}</span>
                    <span style={{ fontSize: 11, color: 'var(--slate-dim)' }}>{article.readTime}</span>
                  </div>
                  <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(17px,2.2vw,21px)', color: 'var(--white)', marginBottom: 8, lineHeight: 1.3 }}>{article.title}</h2>
                  <p style={{ fontSize: 13.5, color: 'var(--slate)', lineHeight: 1.6, margin: 0 }}>{article.metaDesc}</p>
                </div>
                <ArrowRight size={18} color="var(--slate-dim)" style={{ flexShrink: 0, marginTop: 4 }} />
              </div>
            </Link>
          ))}
        </div>

        {/* CTA */}
        <div style={{ marginTop: 64, background: 'var(--navy-card)', border: '1px solid rgba(0,212,170,.15)', borderRadius: 16, padding: 32, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 20 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, color: 'var(--white)', marginBottom: 6 }}>Ready to get MTD-compliant?</div>
            <div style={{ fontSize: 14, color: 'var(--slate)' }}>Try Quarterly free — built by Chartered Accountants for sole traders.</div>
          </div>
          <Link to="/login" className="btn btn-primary btn-lg" style={{ gap: 8, whiteSpace: 'nowrap' }}>
            Try free <ArrowRight size={15} />
          </Link>
        </div>
      </div>
    </div>
  )
}

// ─── ARTICLE PAGE ────────────────────────────────────────────────────────────

function ArticlePage() {
  const { slug } = useParams()
  const article = ARTICLES.find(a => a.slug === slug)

  if (!article) {
    return (
      <div style={{ background: 'var(--navy)', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontFamily: 'var(--font-display)', color: 'var(--white)', marginBottom: 16 }}>Article not found</h1>
          <Link to="/blog" className="btn btn-primary">Back to guides</Link>
        </div>
      </div>
    )
  }

  // Parse markdown-ish content into React elements
  function renderContent(raw) {
    return raw.trim().split('\n\n').map((block, i) => {
      if (block.startsWith('## ')) {
        return <h2 key={i} style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(20px,2.5vw,26px)', color: 'var(--white)', margin: '40px 0 14px', lineHeight: 1.2 }}>{block.replace('## ', '')}</h2>
      }
      if (block.startsWith('**') && block.endsWith('**')) {
        return <p key={i} style={{ fontSize: 15, color: 'var(--white)', fontWeight: 700, lineHeight: 1.7, margin: '0 0 10px' }}>{block.replace(/\*\*/g, '')}</p>
      }
      // Handle inline bold
      const parts = block.split(/(\*\*[^*]+\*\*)/)
      return (
        <p key={i} style={{ fontSize: 15.5, color: 'var(--slate)', lineHeight: 1.85, margin: '0 0 18px' }}>
          {parts.map((part, j) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return <strong key={j} style={{ color: 'var(--white)', fontWeight: 600 }}>{part.slice(2, -2)}</strong>
            }
            return part
          })}
        </p>
      )
    })
  }

  const relatedArticles = ARTICLES.filter(a => a.slug !== slug).slice(0, 3)

  return (
    <div style={{ background: 'var(--navy)', minHeight: '100vh' }}>

      {/* Nav */}
      <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, background: 'rgba(8,18,31,0.94)', backdropFilter: 'blur(18px)', borderBottom: '1px solid var(--navy-border)', padding: '0 clamp(16px,4vw,40px)', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--teal)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: '#08121F', fontWeight: 700 }}>Q</span>
          </div>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: 'var(--white)' }}>Quarterly</span>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Link to="/blog" style={{ fontSize: 13, color: 'var(--slate)', fontWeight: 500, textDecoration: 'none' }}>Guides</Link>
          <Link to="/pricing" style={{ fontSize: 13, color: 'var(--slate)', fontWeight: 500, textDecoration: 'none' }}>Pricing</Link>
          <Link to="/login" className="btn btn-primary btn-sm">Try free</Link>
        </div>
      </nav>

      <article style={{ maxWidth: 720, margin: '0 auto', padding: 'clamp(100px,12vw,130px) clamp(16px,4vw,40px) 80px' }}>

        {/* Back */}
        <Link to="/blog" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--slate)', textDecoration: 'none', marginBottom: 32 }}>
          <ArrowLeft size={13} /> Back to guides
        </Link>

        {/* Header */}
        <div style={{ marginBottom: 40 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <span style={{ fontSize: 11, color: 'var(--teal)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em' }}>{article.category}</span>
            <span style={{ fontSize: 11, color: 'var(--slate-dim)' }}>{article.readTime}</span>
          </div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(28px,4vw,44px)', color: 'var(--white)', lineHeight: 1.1, marginBottom: 20 }}>{article.title}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(0,212,170,.1)', border: '1px solid rgba(0,212,170,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--teal)' }}>M</span>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)' }}>Mubarik Mohamed</div>
              <div style={{ fontSize: 11, color: 'var(--slate-dim)' }}>Chartered Accountant · Quarterly</div>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(0,212,170,.06)', border: '1px solid rgba(0,212,170,.15)', borderRadius: 20, padding: '4px 10px' }}>
              <Shield size={10} color="var(--teal)" />
              <span style={{ fontSize: 11, color: 'var(--teal)', fontWeight: 600 }}>Written by a Chartered Accountant</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ borderTop: '1px solid var(--navy-border)', paddingTop: 36 }}>
          {renderContent(article.content)}
        </div>

        {/* CTA box */}
        <div style={{ marginTop: 56, background: 'var(--navy-card)', border: '1px solid rgba(0,212,170,.15)', borderRadius: 16, padding: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <CheckCircle size={16} color="var(--teal)" />
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--white)' }}>Get MTD-compliant with Quarterly</span>
          </div>
          <p style={{ fontSize: 14, color: 'var(--slate)', lineHeight: 1.6, marginBottom: 18 }}>
            Connect your bank, categorise transactions automatically, and file quarterly to HMRC. Built by Chartered Accountants for UK sole traders and landlords. £14.99/month.
          </p>
          <Link to="/login" className="btn btn-primary" style={{ gap: 8 }}>
            Try Quarterly free <ArrowRight size={14} />
          </Link>
        </div>

        {/* Related articles */}
        <div style={{ marginTop: 56 }}>
          <div style={{ fontSize: 11, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '.12em', fontWeight: 700, marginBottom: 20 }}>More guides</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {relatedArticles.map(rel => (
              <Link key={rel.slug} to={`/blog/${rel.slug}`} style={{ textDecoration: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderBottom: '1px solid var(--navy-border)', gap: 16 }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--teal)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>{rel.category}</div>
                  <div style={{ fontSize: 14, color: 'var(--white)', fontWeight: 500 }}>{rel.title}</div>
                </div>
                <ArrowRight size={15} color="var(--slate-dim)" style={{ flexShrink: 0 }} />
              </Link>
            ))}
          </div>
        </div>

      </article>
    </div>
  )
}

// ─── EXPORTS ─────────────────────────────────────────────────────────────────

export { ARTICLES }
export function BlogIndexPage() { return <BlogIndex /> }
export function BlogArticlePage() { return <ArticlePage /> }
